import { library, dom } from '@fortawesome/fontawesome-svg-core';
import { 
    faEyeDropper, faDropletSlash, faDroplet, faTrashCan, faGlobe, faBars, 
    faFloppyDisk, faChevronLeft, faChevronRight, faUpload, faDownload, 
    faRepeat, faGripLines, faArrowPointer, faPaintbrush, faEraser, 
    faFillDrip, faTableCells, faRotate, faRightLeft, faUpDown, faShuffle, faCopy, faPen, faGear
} from '@fortawesome/free-solid-svg-icons';
import { 
    faFloppyDisk as faFloppyDiskReg, 
    faEnvelope, 
    faTrashCan as faTrashCanReg,
    faSun as faSunReg
} from '@fortawesome/free-regular-svg-icons';

library.add(
    faEyeDropper, faDropletSlash, faDroplet, faTrashCan, faGlobe, faBars, 
    faFloppyDisk, faChevronLeft, faChevronRight, faUpload, faDownload, 
    faRepeat, faGripLines, faArrowPointer, faPaintbrush, faEraser, 
    faFillDrip, faTableCells, faRotate, faRightLeft, faUpDown, faShuffle, faCopy, faPen, faGear,
    faFloppyDiskReg, faEnvelope, faTrashCanReg, faSunReg
);
dom.watch();

// Local
import { SpriteLab } from './components/spriteLab/SpriteLab';
import { translations, supportedLocalesConfig, LocaleData, getBestLocale, loadLocale } from './components/locales';
import { ContentModal, CookiesConsent, LangModal, EmailSupport } from './components';

// Styles
import './styles/variables.css';
import './styles/spriteLab/spriteLab.css';
import './styles/dialogs/dialogs.css';
import './styles/layout/footer.css';
import './styles/dialogs/cookies.css';

class App {
    private locale: string;
    private activeModal: 'lang' | 'content' | 'support' | null = null;
    private currentModalInstance: any = null;
    private spriteLab: SpriteLab;

    constructor() {
        // Determine locale synchronously (reads localStorage / navigator)
        const stored = localStorage.getItem('argvoid_locale');
        this.locale = stored || getBestLocale();

        // Validate the locale actually exists in our supported configs (clears out old cached 'en-US' values)
        if (!supportedLocalesConfig.some(c => c.id === this.locale)) {
            this.locale = getBestLocale();
        }

        // Start the SpriteLab immediately (doesn't need locale)
        this.spriteLab = new SpriteLab();
        this.spriteLab.setTranslator((key) => this.t(key));

        // Load the required locale AND fallback locale async, then finish init
        const localesToLoad = [loadLocale(this.locale)];
        if (this.locale !== 'en') localesToLoad.push(loadLocale('en'));

        Promise.all(localesToLoad).then(() => {
            this.init();
            this.handleDirectLink();
            this.initCookies();
            this.initLangBtn();
            this.initSupportListener();
        });
    }

    private initLangBtn() {
        const langBtn = this.spriteLab.getLangButton();
        if (!langBtn) return;

        // Set initial text
        const cfg = supportedLocalesConfig.find(c => c.id === this.locale) || supportedLocalesConfig[0];
        this.spriteLab.updateLangText(cfg.nativeName);

        langBtn.onclick = () => {
            this.openLangModal();
        };
    }

    private openLangModal() {
        this.closeModal();
        this.activeModal = 'lang';

        const cfg = supportedLocalesConfig.find(c => c.id === this.locale) || supportedLocalesConfig[0];

        this.currentModalInstance = new LangModal({
            onClose: () => this.closeModal(),
            t: (key) => this.t(key),
            locale: this.locale,
            supportedLocalesConfig: supportedLocalesConfig,
            direction: cfg.direction,
            changeLanguage: (newLang: string) => {
                localStorage.setItem('argvoid_locale', newLang);
                window.location.reload();
            }
        });

        document.body.appendChild(this.currentModalInstance.getElement());
    }

    private initCookies() {
        const cfg = supportedLocalesConfig.find(c => c.id === this.locale) || supportedLocalesConfig[0];
        new CookiesConsent({
            t: (key) => this.t(key),
            direction: cfg.direction,
            onOpenPolicy: () => {
                window.history.pushState({ modal: true }, '', '/cookies.html');
                this.openContentModal(this.t('cookies_title'), 'cookies_content');
            }
        });
    }

    private handleDirectLink() {
        const path = window.location.pathname;
        if (path.includes('cookies.html')) {
            this.openContentModal(this.t('cookies_title'), 'cookies_content');
        } else if (path.includes('policy.html')) {
            this.openContentModal(this.t('policy_title'), 'policy_content');
        } else if (path.includes('terms.html')) {
            this.openContentModal(this.t('terms_title'), 'terms_content');
        }
    }

    private t(key: keyof LocaleData) {
        const currentData = translations[this.locale];
        const fallbackData = translations['en'];
        return currentData?.[key] || fallbackData?.[key] || key;
    }

    private init() {
        this.updateDocumentAttributes();
        this.initGoogleAnalytics();
        this.render();
        this.initPopState();
    }

    private updateDocumentAttributes() {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = this.locale;

        const localizedTitle = this.t('title') as string;
        document.title = localizedTitle ? `Sprite Lab - ${localizedTitle}` : 'Sprite Lab - ArgVoid';
    }

    private initGoogleAnalytics() {
        const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;
        if (!measurementId || measurementId === 'G-XXXXXXXXXX') return;

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script);

        const initScript = document.createElement('script');
        initScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${measurementId}');
        `;
        document.head.appendChild(initScript);
    }

    private initPopState() {
        window.addEventListener('popstate', () => {
            if (this.activeModal) {
                this.closeModal();
            }
        });
    }

    private initSupportListener() {
        window.addEventListener('open-support-modal', () => {
            this.openSupportModal();
        });
    }

    private openSupportModal() {
        this.closeModal();
        this.activeModal = 'support';
        const cfg = supportedLocalesConfig.find(c => c.id === this.locale) || supportedLocalesConfig[0];
        this.currentModalInstance = new EmailSupport({
            onClose: () => this.closeModal(),
            t: (key) => this.t(key),
            direction: cfg.direction
        });
        document.body.appendChild(this.currentModalInstance.getElement());
    }

    private render() {
        // Mount SpriteLab
        this.spriteLab.mount(document.body);

        const footer = document.createElement('footer');
        footer.id = 'footer';

        const createFooterLink = (titleKey: keyof LocaleData, contentKey: keyof LocaleData) => {
            const link = document.createElement('a');
            link.className = 'footer-link';
            link.href = `/${titleKey.split('_')[0]}.html`;
            link.textContent = this.t(titleKey);

            link.onclick = (e) => {
                e.preventDefault();
                window.history.pushState({ modal: true }, '', link.href);
                this.openContentModal(this.t(titleKey), contentKey);
            };
            return link;
        };

        footer.appendChild(createFooterLink('terms_title', 'terms_content'));
        footer.appendChild(createFooterLink('policy_title', 'policy_content'));
        footer.appendChild(createFooterLink('cookies_title', 'cookies_content'));

        document.body.appendChild(footer);
    }

    private openContentModal(title: string, contentKey: keyof LocaleData) {
        this.closeModal();
        this.activeModal = 'content';
        const cfg = supportedLocalesConfig.find(c => c.id === this.locale) || supportedLocalesConfig[0];
        this.currentModalInstance = new ContentModal({
            title,
            content: this.t(contentKey),
            isHtml: true,
            direction: cfg.direction,
            onClose: () => {
                if (window.history.state?.modal) {
                    window.history.back();
                } else {
                    this.closeModal();
                }
            }
        });
        document.body.appendChild(this.currentModalInstance.getElement());
    }

    private closeModal() {
        if (this.currentModalInstance) {
            this.currentModalInstance.destroy();
            this.currentModalInstance = null;
        }
        this.activeModal = null;
    }

}

// Start the app
new App();
